import { useState, useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, Trash2, ChevronDown, Clock, CalendarPlus, Check } from "lucide-react";
import { upcomingHolidays, formatHolidayDate } from "@/components/mc/holidays";

export const DAYS = [
  { key: "mon", label: "Monday", short: "Mon" },
  { key: "tue", label: "Tuesday", short: "Tue" },
  { key: "wed", label: "Wednesday", short: "Wed" },
  { key: "thu", label: "Thursday", short: "Thu" },
  { key: "fri", label: "Friday", short: "Fri" },
  { key: "sat", label: "Saturday", short: "Sat" },
  { key: "sun", label: "Sunday", short: "Sun" },
];

export const DEFAULT_HOURS = DAYS.reduce((acc, d) => {
  acc[d.key] = { closed: d.key === "sun", open: "09:00", close: "17:00" };
  return acc;
}, {});

function fmt(t) {
  if (typeof t !== "string" || !t.includes(":")) return "";
  const [h, m] = t.split(":").map(Number);
  if (Number.isNaN(h)) return "";
  const ap = h >= 12 ? "PM" : "AM";
  const hr = h % 12 || 12;
  return `${hr}:${String(m || 0).padStart(2, "0")} ${ap}`;
}

function todayKey() {
  // JS getDay: 0=Sun..6=Sat → map to our keys
  const map = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  return map[new Date().getDay()];
}

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Google-Maps-style: today's status prominently, expandable to full week + holidays
export function HoursDisplay({ hours, holidays = [], accent = "hsl(var(--primary))" }) {
  const [open, setOpen] = useState(false);
  const h = hours && Object.keys(hours).length ? hours : DEFAULT_HOURS;
  const tk = todayKey();
  const today = h[tk] || { closed: true };
  const holidayToday = (holidays || []).find((x) => x.date === todayIso());

  let statusLabel, statusColor, todayText;
  if (holidayToday) {
    statusLabel = "Closed"; statusColor = "hsl(var(--destructive))";
    todayText = holidayToday.note ? `Closed · ${holidayToday.note}` : "Closed (holiday)";
  } else if (today.closed) {
    statusLabel = "Closed"; statusColor = "hsl(var(--destructive))";
    todayText = "Closed today";
  } else {
    statusLabel = "Open"; statusColor = "hsl(var(--success))";
    todayText = `${fmt(today.open)} – ${fmt(today.close)}`;
  }

  return (
    <div className="border border-border" data-testid="hours-display">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-secondary/50 transition-colors"
        data-testid="hours-display-toggle">
        <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-semibold" style={{ color: statusColor }}>{statusLabel}</span>
        <span className="text-sm text-muted-foreground">· {todayText}</span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-border px-4 py-3 space-y-1.5" data-testid="hours-display-week">
          {DAYS.map((d) => {
            const row = h[d.key] || { closed: true };
            const isToday = d.key === tk;
            return (
              <div key={d.key} className={`flex justify-between text-sm ${isToday ? "font-semibold" : "text-muted-foreground"}`}>
                <span>{d.label}</span>
                <span>{row.closed ? "Closed" : `${fmt(row.open)} – ${fmt(row.close)}`}</span>
              </div>
            );
          })}
          {(holidays || []).length > 0 && (
            <div className="pt-2 mt-1 border-t border-border">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Holidays / closures</div>
              {holidays.map((x, i) => (
                <div key={i} className="flex justify-between text-sm text-muted-foreground">
                  <span>{x.date}</span><span>{x.note || "Closed"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function HoursEditor({ hours, holidays, onChange }) {
  const h = hours && Object.keys(hours).length ? hours : DEFAULT_HOURS;
  const hol = holidays || [];

  const setDay = (key, patch) => onChange({ ...h, [key]: { ...h[key], ...patch } }, hol);
  const addHoliday = () => onChange(h, [...hol, { date: todayIso(), note: "" }]);
  const setHoliday = (i, patch) => onChange(h, hol.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const removeHoliday = (i) => onChange(h, hol.filter((_, idx) => idx !== i));
  const addFromList = (item) => {
    if (hol.some((x) => x.date === item.date)) return;
    onChange(h, [...hol, { date: item.date, note: item.name }]);
  };
  const upcoming = useMemo(() => upcomingHolidays(), []);

  return (
    <div className="space-y-4" data-testid="hours-editor">
      <div className="space-y-2">
        {DAYS.map((d) => {
          const row = h[d.key] || { closed: true, open: "09:00", close: "17:00" };
          return (
            <div key={d.key} className="flex items-center gap-3 flex-wrap" data-testid={`hours-row-${d.key}`}>
              <span className="w-24 text-sm font-medium">{d.label}</span>
              <div className="flex items-center gap-2">
                <Switch checked={!row.closed} onCheckedChange={(v) => setDay(d.key, { closed: !v })} data-testid={`hours-open-${d.key}`} />
                <span className="text-xs text-muted-foreground w-12">{row.closed ? "Closed" : "Open"}</span>
              </div>
              {!row.closed && (
                <div className="flex items-center gap-2">
                  <Input type="time" className="rounded-none h-8 w-32" value={row.open || "09:00"}
                    onChange={(e) => setDay(d.key, { open: e.target.value })} data-testid={`hours-open-time-${d.key}`} />
                  <span className="text-muted-foreground text-sm">–</span>
                  <Input type="time" className="rounded-none h-8 w-32" value={row.close || "17:00"}
                    onChange={(e) => setDay(d.key, { close: e.target.value })} data-testid={`hours-close-time-${d.key}`} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="border-t border-border pt-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-sm font-medium">Holidays &amp; special closures</div>
            <p className="text-xs text-muted-foreground">Dates you'll be closed (shown to customers).</p>
          </div>
          <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" className="rounded-none gap-1.5" onClick={addHoliday} data-testid="add-holiday-btn">
            <Plus className="h-3.5 w-3.5" /> Custom
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" size="sm" className="rounded-none gap-1.5" data-testid="pick-holiday-btn">
                <CalendarPlus className="h-3.5 w-3.5" /> Pick a holiday
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0 rounded-none" data-testid="holiday-picker">
              <div className="px-3 py-2 border-b border-border">
                <div className="text-sm font-semibold">Upcoming holidays</div>
                <p className="text-[11px] text-muted-foreground">Tap to add a closure. Dates are auto-calculated each year.</p>
              </div>
              <ScrollArea className="h-72">
                <div className="py-1">
                  {upcoming.map((item) => {
                    const added = hol.some((x) => x.date === item.date);
                    return (
                      <button key={`${item.date}-${item.name}`} type="button" disabled={added}
                        onClick={() => addFromList(item)}
                        className={`w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-secondary/60 transition-colors ${added ? "opacity-50 cursor-default" : ""}`}
                        data-testid={`holiday-option-${item.date}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{item.name}</div>
                          <div className="text-[11px] text-muted-foreground">{formatHolidayDate(item.date)} · {item.rule}</div>
                        </div>
                        {added && <Check className="h-4 w-4 text-[hsl(var(--success))] shrink-0 mt-0.5" />}
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            </PopoverContent>
          </Popover>
          </div>
        </div>
        <div className="space-y-2">
          {hol.map((x, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`holiday-row-${i}`}>
              <Input type="date" className="rounded-none h-8 w-40" value={x.date}
                onChange={(e) => setHoliday(i, { date: e.target.value })} data-testid={`holiday-date-${i}`} />
              <Input className="rounded-none h-8 flex-1" placeholder="Note (e.g. Christmas)" value={x.note || ""}
                onChange={(e) => setHoliday(i, { note: e.target.value })} data-testid={`holiday-note-${i}`} />
              <button type="button" onClick={() => removeHoliday(i)} className="text-muted-foreground hover:text-destructive" data-testid={`holiday-remove-${i}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {!hol.length && <p className="text-xs text-muted-foreground">No holidays added.</p>}
        </div>
      </div>

      <div className="border-t border-border pt-4">
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Customer preview</div>
        <HoursDisplay hours={h} holidays={hol} />
      </div>
    </div>
  );
}
